import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { AlertTriangle, EyeOff } from 'lucide-react';

import { getProfile } from './config/profileApi';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Tabs from '../../components/ui/Tabs';
import CandidateOverviewTab from './tab-pages/CandidateOverviewTab';
import CandidateApplicationsTab from './tab-pages/CandidateApplicationsTab';
import CandidateNotesTab from './tab-pages/CandidateNotesTab';
import CandidateTagsTab from './tab-pages/CandidateTagsTab';
import CandidateDocumentsTab from './tab-pages/CandidateDocumentsTab';
import CandidateConsentTab from './tab-pages/CandidateConsentTab';

// ADAY DETAY SAYFASI (SPEC F1) — `/ats/candidates/:id`. Şablon: başvuru detay
// sayfası (Breadcrumbs + Tabs + TabContent). Sekmeler: Genel, Başvurular, Notlar,
// Etiketler, Belgeler, KVKK/Rıza.
//
// Yazma aksiyonlarının kaynağı SUNUCUNUN bayrağıdır (`profile.canWrite`): AT.Write/admin.
// `isPanelOnlyViewer` = yalnız mülakat paneli üzerinden gören kullanıcı; sunucu iletişim
// verisini REDAKTE eder, ekran bunu bir bantla açıkça söyler (boş alan "veri yok" sanılmasın).
//
// FE-23 dersi: api.js GET'lerde reject ETMEZ; okuma başarısızsa sekmeler sonsuz
// "Yükleniyor" çizmez, hata kartı çizilir.

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

const StickyHeader = styled.div`
  flex-shrink: 0;
`;

const TabContent = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-top: 8px;
`;

const Banner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding: 10px 14px;
  border-radius: 6px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.bgColor};
`;

const ErrorCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  margin: 24px 0;
  padding: 18px 20px;
  border-radius: ${({ theme }) => theme.metrics.radius.large};
  border: 1px solid ${({ theme }) => theme.colors.auxiliar.danger}33;
  background: ${({ theme }) => theme.colors.auxiliar.danger}14;
  color: ${({ theme }) => theme.colors.auxiliar.danger};
`;

const ErrorHeadline = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  font-size: ${({ theme }) => theme.metrics.font.size.md};
`;

const ErrorHint = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const BackLink = styled(Link)`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.primary.main};
`;

export const CANDIDATE_TABS = ['overview', 'applications', 'notes', 'tags', 'documents', 'consent'];

export default function AtsCandidateDetailsPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [profile, setProfile] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const loadProfile = useCallback(() => {
    getProfile(id).then((p) => {
      setLoadFailed(p == null);
      setProfile(p);
    });
  }, [id]);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const writable = profile?.canWrite === true;
  const isAnonymized = !!profile?.anonymizedAt;

  const tabs = useMemo(() => CANDIDATE_TABS.map((key) => ({
    key,
    label: t(`ats.candidateProfile.tab.${key}`),
  })), [t]);

  return (
    <PageContainer>
      <StickyHeader>
        <Breadcrumbs items={[
          { label: t('ats.candidate.pageTitle'), path: '/ats/candidates' },
          { label: loadFailed ? t('ats.candidateProfile.notFoundTitle') : (profile?.fullName || '...') },
        ]} />
        {!loadFailed && <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />}
      </StickyHeader>

      <TabContent data-role="candidate-tab-content" data-tab={activeTab}>
        {loadFailed && (
          <ErrorCard role="alert" data-role="ats-candidate-load-error">
            <ErrorHeadline>
              <AlertTriangle size={18} />
              {t('ats.candidateProfile.notFoundTitle')}
            </ErrorHeadline>
            <ErrorHint>{t('ats.candidateProfile.notFoundHint')}</ErrorHint>
            <BackLink to="/ats/candidates">{t('ats.candidateProfile.backToList')}</BackLink>
          </ErrorCard>
        )}

        {!loadFailed && profile?.isPanelOnlyViewer && (
          <Banner data-role="candidate-panel-only-banner">
            <EyeOff size={16} />
            {t('ats.candidateProfile.panelOnlyHint')}
          </Banner>
        )}

        {!loadFailed && isAnonymized && (
          <Banner data-role="candidate-anonymized-banner">
            <EyeOff size={16} />
            {t('ats.candidateProfile.anonymizedHint')}
          </Banner>
        )}

        {!loadFailed && profile && activeTab === 'overview' && (
          <CandidateOverviewTab profile={profile} writable={writable && !isAnonymized} onChanged={loadProfile} />
        )}
        {!loadFailed && profile && activeTab === 'applications' && (
          <CandidateApplicationsTab candidateId={id} />
        )}
        {!loadFailed && profile && activeTab === 'notes' && (
          <CandidateNotesTab candidateId={id} writable={writable} readOnly={isAnonymized} />
        )}
        {!loadFailed && profile && activeTab === 'tags' && (
          <CandidateTagsTab
            candidateId={id}
            profile={profile}
            writable={writable && !isAnonymized}
            onChanged={loadProfile}
          />
        )}
        {!loadFailed && profile && activeTab === 'documents' && (
          <CandidateDocumentsTab candidateId={id} writable={writable && !isAnonymized} />
        )}
        {!loadFailed && profile && activeTab === 'consent' && (
          <CandidateConsentTab
            candidateId={id}
            profile={profile}
            writable={writable && !isAnonymized}
            onChanged={loadProfile}
          />
        )}
      </TabContent>
    </PageContainer>
  );
}
